import {useMemo} from 'react';

import {useCachedDataWithOrigin} from '../../../util/cache';
import * as QueryTypes from '../../../util/queryTypes';
import {useDetectPanelVariant} from '../../Panel/hooks/hooks';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {useIsRenderingInPopup} from '../../Panel/PanelEditor/SinglePanelInspectorContainer';
import {PanelPopupMode} from '../../Panel/types';
import {useBucketedData} from '../bucketedData/useBucketedData';
import {Range} from '../common';
import {RunsLinePlotConfig} from '../types';
type CachedBucketedDataProps = {
  pageQuery: QueryTypes.Query;
  runsLinePlotConfig: RunsLinePlotConfig;
  queryZoomRange: Range;
  numBuckets?: number;
};
export const useCachedBucketedData = ({
  pageQuery,
  runsLinePlotConfig,
  queryZoomRange,
  numBuckets,
}: CachedBucketedDataProps) => {
  const {panelPopupMode} = usePanelContext();
  const isRenderingInPopup = useIsRenderingInPopup();
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const isFullscreenMode =
    (isRenderingInPopup && panelPopupMode === PanelPopupMode.FULL_SCREEN) ||
    isFullScreenPanelPage;
  /**
   * `waitUntilPanelMeasurement` is a hack to delay launching the queries until we get the initial panel measurements because panel dimensions are calculated after the first render, and the first render would dispatch the queries at full resolution, we need to force a loading state to keep the panel from erroring and
   */

  const waitUntilPanelMeasurement = numBuckets ? false : true;

  const oneKData = useBucketedData(
    runsLinePlotConfig,
    pageQuery,
    queryZoomRange,
    numBuckets ?? 1000,
    waitUntilPanelMeasurement
  );

  const tenKData = useBucketedData(
    runsLinePlotConfig,
    pageQuery,
    queryZoomRange,
    10000,
    waitUntilPanelMeasurement || !isFullscreenMode
  );

  const {data: cachedData1k, origin} = useCachedDataWithOrigin<
    typeof oneKData.data
  >({
    namespace: 'BucketedRunsDeltaQuery',
    key: oneKData.key,
    dataFromServer: oneKData.data,
    waitingForDataFromServer: oneKData.loading,
  });

  let data1k = oneKData.data;
  let loading1k = oneKData.loading;
  if (origin === 'cache' && cachedData1k.histories.data.length > 0) {
    data1k = cachedData1k;
    loading1k = false;
  }

  const has10kData = useMemo(
    () =>
      tenKData.data.histories.data.some(
        run => run.history && run.history.length > 0
      ),
    [tenKData.data.histories.data]
  );

  // Memoize the data to provide stable references
  const stableData = useMemo(() => {
    if (isFullscreenMode) {
      // only use the 1k data if we're waiting for the initial hit of the 10k data to load
      return has10kData ? tenKData.data : data1k;
    }
    return data1k;
  }, [isFullscreenMode, tenKData.data, data1k, has10kData]);

  // Compute loading states
  const isLoading = useMemo(() => {
    if (isFullscreenMode) {
      return tenKData.loading || waitUntilPanelMeasurement;
    }
    return loading1k || waitUntilPanelMeasurement;
  }, [
    isFullscreenMode,
    tenKData.loading,
    loading1k,
    waitUntilPanelMeasurement,
  ]);

  // Compute error state
  const error = useMemo(() => {
    if (isFullscreenMode) {
      return tenKData.error || oneKData.error;
    }
    return oneKData.error;
  }, [isFullscreenMode, tenKData.error, oneKData.error]);

  // Memoize the entire return object to prevent unnecessary re-renders
  // when only loading state changes but data remains the same
  return useMemo(
    () => ({
      data: stableData,
      loading: isLoading,
      error,
    }),
    [stableData, isLoading, error]
  );
};
